import { SideModal } from '@design-system/components/modal/sideModal';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import {
  CommentsViewModel,
  useCommentsViewModel,
} from './model/comments.viewModel';
import * as css from './comments.css';
import { CommentCard } from './components/CommentCard';
import { CommentInput } from './components/CommentInput';
import { EmptyState } from './components/EmptyState';
import { LoadingState } from './components/LoadingState';
import { NavigationHeader } from './components/NavigationHeader';
import { Typography } from '@design-system/components/typography/typography';
import { useRef, useEffect } from 'react';

const CommentsListView = (vm: CommentsViewModel) => {
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const commentsContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Automatycznie scroll na dół gdy zmienią się komentarze
    if (commentsContentRef.current) {
      commentsContentRef.current.scrollTop =
        commentsContentRef.current.scrollHeight;
    }
  }, [vm.displayedComments.length]);

  return (
    <div className={css.viewContent}>
      <div className={css.commentsContent} ref={commentsContentRef}>
        {vm.displayedComments.length === 0 ? (
          <EmptyState
            title="No visible comments"
            description="Visible comments will appear here when users start engaging with this content."
          />
        ) : (
          vm.displayedComments.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              onVisibilityToggle={(isVisible) =>
                vm.dispatch('ToggleVisibility', {
                  commentId: comment.id,
                  isVisible,
                })
              }
              onThreadClick={() =>
                vm.dispatch('NavigateToThread', { commentId: comment.id })
              }
              onDelete={() =>
                vm.dispatch('DeleteComment', { commentId: comment.id })
              }
            />
          ))
        )}
        <div ref={commentsEndRef} />
      </div>
      <div className={css.commentInputContainer}>
        <CommentInput
          placeholder="Write a comment..."
          onSubmit={async (text) => vm.dispatch('CreateComment', { text })}
          isLoading={vm.isCreatingComment}
        />
      </div>
    </div>
  );
};

const HiddenCommentsView = (vm: CommentsViewModel) => (
  <div className={css.viewContent}>
    <div className={css.commentsContent}>
      {vm.displayedComments.length === 0 ? (
        <EmptyState
          title="No hidden comments"
          description="Hidden comments will appear here when you hide comments."
        />
      ) : (
        vm.displayedComments.map((comment) => (
          <CommentCard
            key={comment.id}
            comment={comment}
            onVisibilityToggle={(isVisible) =>
              vm.dispatch('ToggleVisibility', {
                commentId: comment.id,
                isVisible,
              })
            }
            onThreadClick={() =>
              vm.dispatch('NavigateToThread', { commentId: comment.id })
            }
            onDelete={() =>
              vm.dispatch('DeleteComment', { commentId: comment.id })
            }
          />
        ))
      )}
    </div>
  </div>
);

const ThreadHiddenView = (vm: CommentsViewModel) => (
  <div className={css.viewContent}>
    <div className={css.commentsContent}>
      {vm.displayedComments.length === 0 ? (
        <EmptyState
          title="No hidden replies"
          description="Hidden replies to this comment will appear here."
        />
      ) : (
        vm.displayedComments.map((comment) => (
          <CommentCard
            key={comment.id}
            comment={comment}
            onVisibilityToggle={(isVisible) =>
              vm.dispatch('ToggleVisibility', {
                commentId: comment.id,
                isVisible,
              })
            }
            onThreadClick={undefined}
            onDelete={() =>
              vm.dispatch('DeleteComment', { commentId: comment.id })
            }
            showThreadButton={false}
            isInThread={true}
          />
        ))
      )}
    </div>
  </div>
);

const ThreadView = (vm: CommentsViewModel) => {
  const commentsContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Automatycznie scroll na dół gdy zmienią się odpowiedzi
    if (commentsContentRef.current) {
      commentsContentRef.current.scrollTop =
        commentsContentRef.current.scrollHeight;
    }
  }, [vm.displayedComments.length]);

  return (
    <div className={css.viewContent}>
      {vm.openThreadComment && (
        <div className={css.commentsContent} ref={commentsContentRef}>
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <CommentCard
              comment={vm.openThreadComment}
              onVisibilityToggle={(isVisible) =>
                vm.dispatch('ToggleVisibility', {
                  commentId: vm.openThreadComment?.id || '',
                  isVisible,
                })
              }
              onThreadClick={undefined}
              onDelete={() =>
                vm.dispatch('DeleteComment', {
                  commentId: vm.openThreadComment?.id || '',
                })
              }
              showThreadButton={false}
              isInThread={true}
            />
            <div className={css.repliesCount}>
              <Typography
                variant="body"
                size="small"
                fontWeight="normal"
                className={css.repliesCountText}
              >
                {vm.threadVisibleCount}{' '}
                {vm.threadVisibleCount === 1 ? 'reply' : 'replies'}
              </Typography>
            </div>
          </div>

          {vm.displayedComments.length === 0 ? (
            <EmptyState
              title="No visible replies"
              description="Visible replies to this comment will appear here."
            />
          ) : (
            vm.displayedComments.map((comment) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                onVisibilityToggle={(isVisible) =>
                  vm.dispatch('ToggleVisibility', {
                    commentId: comment.id,
                    isVisible,
                  })
                }
                onThreadClick={undefined}
                onDelete={() =>
                  vm.dispatch('DeleteComment', { commentId: comment.id })
                }
                showThreadButton={false}
                isInThread={true}
              />
            ))
          )}
        </div>
      )}

      {vm.openThreadId && (
        <div className={css.commentInputContainer}>
          <CommentInput
            placeholder="Reply to this thread..."
            onSubmit={async (text) =>
              vm.dispatch('CreateThreadReply', {
                threadId: vm.openThreadId,
                text,
              })
            }
            isLoading={vm.isCreatingReply}
          />
        </div>
      )}
    </div>
  );
};

const renderCurrentView = (vm: CommentsViewModel) => {
  switch (vm.viewMode) {
    case 'hidden':
      return <HiddenCommentsView {...vm} />;
    case 'thread':
      return <ThreadView {...vm} />;
    case 'thread-hidden':
      return <ThreadHiddenView {...vm} />;
    case 'comments':
    default:
      return <CommentsListView {...vm} />;
  }
};

const CommentsContent = (vm: CommentsViewModel) => {
  return (
    <div className={css.commentsContainer}>
      <NavigationHeader
        viewMode={vm.viewMode}
        canGoBack={vm.canGoBack}
        onBack={() => vm.dispatch('NavigateBack')}
        hiddenCount={vm.hiddenCount}
        visibleCount={vm.visibleCount}
        threadHiddenCount={vm.threadHiddenCount}
        threadVisibleCount={vm.threadVisibleCount}
        shouldShowHiddenChip={vm.shouldShowHiddenChip}
        shouldShowThreadHiddenChip={vm.shouldShowThreadHiddenChip}
        onNavigateToHidden={() => vm.dispatch('NavigateToHidden')}
        onNavigateToThreadHidden={() => vm.dispatch('NavigateToThreadHidden')}
      />

      {renderCurrentView(vm)}
    </div>
  );
};
const CommentsContentView = ({ contentId }: { contentId: string | null }) => {
  const commentsVM = useCommentsViewModel({ contentId });

  if (!contentId) {
    return null;
  }
  return (
    <AsyncRenderer
      data={commentsVM}
      renderer={CommentsContent}
      loadRender={<LoadingState />}
    />
  );
};

export const CommentsSideModal = (p: {
  contentId?: string;
  onClose: () => void;
}) => (
  <SideModal isOpen={!!p.contentId} onClose={p.onClose} noSpacing>
    <CommentsContentView contentId={p.contentId || null} />
  </SideModal>
);
